/**
 * Per-finger anatomical parameters — single source of truth.
 *
 * Used by the constraint system (MCP cone limits), curl slider (flexion
 * trajectory), and hand-pose presets (full-curl angles).  PIP/DIP hinge
 * limits are mechanical maximums and stay hardcoded in constraints.ts.
 *
 * Sources:
 *   MCP/PIP ROM — fist-closure medians (Hahn et al., 2012)
 *   DIP/PIP coupling — extensor assembly ratios (Lin et al., 2011)
 *   Thumb CMC — CT-based in-vivo kinematics (Halilaj et al., 2014)
 *
 * The ulnar gradient (increasing ROM from index → pinky) is a well-documented
 * anatomical feature: the pinky's MCP is more mobile than the index's.
 */

export interface FingerSpec {
  /** Max MCP/CMC flexion (degrees) at natural full curl. */
  mcpFlex: number;
  /** Max PIP/IP flexion (degrees) at natural full curl. */
  pipFlex: number;
  /** Max DIP flexion (degrees) at natural full curl. */
  dipFlex: number;
  /**
   * DIP-to-PIP coupling ratio for intermediate curl positions.
   * At partial curl the extensor assembly links DIP to PIP
   * (DIP ≈ ratio × PIP).  At full curl, dipFlex is used directly
   * since the DIP can exceed the coupled prediction.
   *
   * The thumb lacks this tendon linkage so its ratio is 0 (unused).
   */
  dipRatio: number;
  /** Max MCP/CMC abduction (splay) in degrees. */
  abduction: number;
}

/**
 * Per-finger anatomical specs.
 *
 * Thumb values describe the CMC/IP chain and use the compound curl axis.
 * Index–pinky follow the ulnar gradient for MCP and DIP coupling.
 */
export const FINGER_SPECS: Readonly<Record<string, FingerSpec>> = {
  //                          MCP°   PIP°   DIP°   DIP/PIP  abd°
  thumb: { mcpFlex: 29, pipFlex: 57, dipFlex: 12, dipRatio: 0, abduction: 55 },
  index: {
    mcpFlex: 79,
    pipFlex: 90,
    dipFlex: 75,
    dipRatio: 0.77,
    abduction: 35,
  },
  middle: {
    mcpFlex: 86,
    pipFlex: 90,
    dipFlex: 75,
    dipRatio: 0.75,
    abduction: 35,
  },
  ring: {
    mcpFlex: 90,
    pipFlex: 90,
    dipFlex: 75,
    dipRatio: 0.75,
    abduction: 35,
  },
  pinky: {
    mcpFlex: 97,
    pipFlex: 90,
    dipFlex: 75,
    dipRatio: 0.57,
    abduction: 35,
  },
};
